import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ExportSignedInvoicesDocument,
  SignedInvoicesPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import LabeledFancyCheckbox from 'modules/core/components/Inputs/LabeledFancyCheckbox'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FormConfiguration from './Components/FormConfiguration'

const formatList = [
  {
    label: 'CSV',
    value: 'csv',
  },
  {
    label: 'XLSX',
    value: 'xlsx',
  },
]

interface ModalProps {
  onClose: () => void
}

const SignedInvoiceExportModal = ({ onClose }: ModalProps) => {
  const { company, access } = useCurrentSession()
  const mainLegalEntityId: number | undefined = company.legalEntities.find(
    (legalEntity) => legalEntity.main
  )?.id

  const form = React.useRef(new FormConfiguration({ company_id: company.id })).current

  const [exportSignedInvoices, { loading: submitting }] = useMutation(
    ExportSignedInvoicesDocument,
    {
      extractorFn: (data) => data.expensesMutations.exportExpensesSignedInvoiceExport,
    }
  )

  const onSubmit = async () => {
    const { from_date, to_date, export_format, with_files, company_id } = form.data()
    const result = await exportSignedInvoices({
      variables: {
        accessId: access.id,
        companyId: company_id,
        exportFormat: export_format,
        fromDate: from_date,
        toDate: to_date,
        withFiles: with_files,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: SignedInvoicesPageDocument,
          variables: {
            companyId: company.id,
            legalEntityId: mainLegalEntityId,
          },
        },
      ],
    })

    const handled = handleResult(result, (response) => {
      if (response.messages) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses.settings.signed_invoices.modal.export.success'),
      })

      onClose()
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('expenses.settings.signed_invoices.modal.export.title')}
      description={i18n.t('expenses.settings.signed_invoices.modal.export.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={submitting || form.mandatoryFieldsEmpty} submit>
          {i18n.t('expenses.settings.signed_invoices.modal.export.submit')}
        </Button>
      }
    >
      <>
        <FormRow>
          <Select
            mandatory
            none={i18n.t('common.none')}
            label={i18n.t('expenses.settings.signed_invoices.modal.export.format')}
            field={form.get('export_format')}
            options={formatList}
          />
        </FormRow>
        <FormRow>
          <Box flexDirection="row" gap="s16">
            <DayPicker
              mandatory
              field={form.get('from_date')}
              label={i18n.t('expenses.settings.signed_invoices.modal.export.from_date')}
              maxDate={moment().endOf('day')}
            />
            <DayPicker
              mandatory
              field={form.get('to_date')}
              label={i18n.t('expenses.settings.signed_invoices.modal.export.to_date')}
              maxDate={moment().endOf('day')}
            />
          </Box>
        </FormRow>
        <FormRow>
          <LabeledFancyCheckbox
            label={i18n.t('expenses.settings.signed_invoices.modal.export.include_files')}
            field={form.get('with_files')}
            color="grey1000"
            size="200"
          />
        </FormRow>
      </>
    </Modal>
  )
}

export default observer(SignedInvoiceExportModal)
